import { useMemo } from "react";
import { Controller, useWatch, type UseFormReturn } from "react-hook-form";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DeliveryCostInput } from "@/components/DeliveryCostInput";

import {
  Field,
  buildDeliveryLocationOptions,
  type Trader,
  type CustomExchangeLocation,
  type ExchangeLocationType,
  type AdministrativeAreaOption,
} from "../../../DirectOfferDetails/OfferForm";
import { getErrors } from "../../../../utils";
import { requiresDeliveryCost } from "../../../../utils/delivery-cost";
import {
  AggregatorSelection,
  type AggregatorOption,
  type AdminAreaNode,
} from "../AggregatorSelection";
import type { DemandFormValues } from "../../schema";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

interface DeliveryStepProps {
  methods: UseFormReturn<DemandFormValues>;
  trader: Trader;
  customExchangeLocations: CustomExchangeLocation[];
  administrativeAreas: AdministrativeAreaOption[];
  exchangeLocationTypes: ExchangeLocationType[];
  aggregators: AggregatorOption[];
  administrativeAreasTree: AdminAreaNode[];
  isFetchingAggregators: boolean;
  selectedAggregator: AggregatorOption | null;
  onSelectAggregator: (aggregator: AggregatorOption) => void;
  fieldErrors: unknown;
  aggregatorErrorMessage?: string;
  currencySymbol?: string;
  t: TranslateFn;
}

const star = (label: string) => `${label} *`;

export const DeliveryStep = ({
  methods,
  trader,
  customExchangeLocations,
  administrativeAreas,
  exchangeLocationTypes,
  aggregators,
  administrativeAreasTree,
  isFetchingAggregators,
  selectedAggregator,
  onSelectAggregator,
  fieldErrors,
  aggregatorErrorMessage,
  currencySymbol,
  t,
}: DeliveryStepProps) => {
  const {
    control,
    formState: { errors },
  } = methods;
  const deliveryLocation = useWatch({ control, name: "delivery_location" });

  const deliveryLocationOptions = useMemo(
    () =>
      buildDeliveryLocationOptions(
        t,
        trader,
        selectedAggregator?.physical_address,
        customExchangeLocations,
        administrativeAreas,
        exchangeLocationTypes
      ),
    [
      t,
      trader,
      selectedAggregator,
      customExchangeLocations,
      administrativeAreas,
      exchangeLocationTypes,
    ]
  );

  const errorFor = (name: keyof DemandFormValues, serverKey?: string) =>
    (errors[name]?.message as string | undefined) ||
    getErrors(fieldErrors, serverKey ?? name) ||
    "";

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("marketOfferDetails.form.deliveryTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <Controller
            control={control}
            name="exchange_date"
            render={({ field }) => (
              <div className="min-w-56 flex-1">
                <Field
                  id="exchange_date"
                  label={star(t("marketOfferDetails.form.deliveryDate"))}
                  errorMessage={errorFor("exchange_date")}
                >
                  <DateTimePicker
                    id="exchange_date"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("marketOfferDetails.form.selectOption")}
                    timeAriaLabel={t("marketOfferDetails.form.deliveryDate")}
                    invalid={!!errorFor("exchange_date")}
                  />
                </Field>
              </div>
            )}
          />

          <Controller
            control={control}
            name="delivery_location"
            render={({ field }) => {
              const selectedIndex = deliveryLocationOptions.findIndex(
                option => option === field.value
              );
              return (
                <div className="min-w-56 flex-1">
                  <Field
                    id="delivery_location"
                    label={star(t("marketOfferDetails.form.deliveryLocation"))}
                    errorMessage={errorFor("delivery_location")}
                    helperText={
                      field.value?.value === "pick_from_aggregator"
                        ? t("directOfferDetails.form.pickUpNoDeliveryCost")
                        : undefined
                    }
                  >
                    <Select
                      value={selectedIndex >= 0 ? String(selectedIndex) : undefined}
                      onValueChange={value =>
                        field.onChange(deliveryLocationOptions[Number(value)])
                      }
                    >
                      <SelectTrigger id="delivery_location" className="w-full">
                        <SelectValue placeholder={t("marketOfferDetails.form.selectOption")} />
                      </SelectTrigger>
                      <SelectContent position="popper">
                        {deliveryLocationOptions.map((option, index) => (
                          <SelectItem key={index} value={String(index)}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              );
            }}
          />

          {trader.is_wfp_vendor && (
            <Controller
              control={control}
              name="contract_number"
              render={({ field }) => (
                <div className="min-w-56 flex-1">
                  <Field
                    id="contract_number"
                    label={t("marketOfferDetails.form.contractNumber")}
                    errorMessage={errorFor("contract_number")}
                  >
                    <Input {...field} id="contract_number" value={field.value ?? ""} />
                  </Field>
                </div>
              )}
            />
          )}
        </CardContent>
      </Card>

      {requiresDeliveryCost(deliveryLocation?.value) && (
        <Card>
          <CardHeader>
            <CardTitle>{t("marketOfferDetails.form.deliveryCostTitle")}</CardTitle>
            <p className="text-muted-foreground text-sm">
              {t("marketOfferDetails.form.deliveryCostSubtitle")}
            </p>
          </CardHeader>
          <CardContent>
            <Controller
              control={control}
              name="delivery_cost"
              render={({ field }) => (
                <div className="w-full sm:w-56">
                  <Field
                    id="delivery_cost"
                    label={star(t("marketOfferDetails.form.deliveryCostLabel"))}
                    errorMessage={errorFor("delivery_cost")}
                  >
                    <DeliveryCostInput
                      {...field}
                      id="delivery_cost"
                      currencySymbol={currencySymbol}
                      value={field.value ?? ""}
                      invalid={!!errorFor("delivery_cost")}
                    />
                  </Field>
                </div>
              )}
            />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t("marketOfferDetails.form.notesTitle")}</CardTitle>
          <p className="text-muted-foreground text-sm">
            {t("marketOfferDetails.form.notesSubtitle")}
          </p>
        </CardHeader>
        <CardContent>
          <Controller
            control={control}
            name="comment"
            render={({ field }) => (
              <Field
                id="comment"
                label={t("marketOfferDetails.form.customMessage")}
                errorMessage={errorFor("comment")}
              >
                <Textarea {...field} id="comment" rows={4} value={field.value ?? ""} />
              </Field>
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("marketOfferDetails.form.aggregatorsTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          <AggregatorSelection
            aggregators={aggregators}
            administrativeAreasTree={administrativeAreasTree}
            isFetching={isFetchingAggregators}
            selectedAggregatorId={selectedAggregator?.id}
            onSelect={onSelectAggregator}
            errorMessage={aggregatorErrorMessage}
          />
        </CardContent>
      </Card>
    </div>
  );
};
